export interface BlogSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface BlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  excerpt: string;
  datePublished: string;
  readingMinutes: number;
  keywords: string[];
  intro: string;
  sections: BlogSection[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: "car-rental-no-deposit-romania",
    title: "Car Rental with No Deposit in Romania: How It Works in 2026",
    metaTitle: "Car Rental No Deposit Romania — How It Works (2026 Guide)",
    description:
      "How no-deposit car rental works in Romania: zero security deposit, no credit card blocked, what you need to book in Bucharest and what is included in the price.",
    excerpt:
      "Most rental companies in Romania block €300–€1,500 on a credit card. Here is how a genuine no-deposit rental works, and what to check before you book.",
    datePublished: "2026-01-14",
    readingMinutes: 6,
    keywords: [
      "car rental no deposit romania",
      "rent a car without deposit bucharest",
      "no security deposit car hire romania",
    ],
    intro:
      "A security deposit is the single biggest friction point when renting a car in Romania. Traditional agencies pre-authorise between €300 and €1,500 on a credit card and hold it for up to 30 days after the rental ends. At carentals.ro we do not take a security deposit and we do not block any amount on your card — you pay the rental price and nothing else.",
    sections: [
      {
        heading: "What a security deposit normally is",
        paragraphs: [
          "A deposit (also called a franchise hold or pre-authorisation) is an amount frozen on your credit card as a guarantee against damage, fines or fuel differences. The money is not spent, but it is unavailable to you — which is a real problem when you are travelling on a budget or your card limit is small.",
          "Deposits in Romania are typically €300–€500 for economy cars and €1,000–€1,500 for premium and luxury models. Release times of 14–30 days after the return are normal.",
        ],
      },
      {
        heading: "How no-deposit car rental works at carentals.ro",
        paragraphs: [
          "We replaced the deposit with full insurance and a simple, transparent contract. You book online, we confirm by email, and you collect the car in Bucharest with no money frozen on any card.",
        ],
        bullets: [
          "Zero security deposit — nothing is blocked, nothing is held after the return.",
          "No credit card required — a debit card or cash payment is accepted.",
          "Full insurance (CASCO + RCA) is included in the daily rate.",
          "Rovinieta (road vignette) is included on every car.",
          "Free delivery in Bucharest and at Otopeni Airport.",
        ],
      },
      {
        heading: "What you need to book",
        paragraphs: [
          "The paperwork stays short because we do not run a card pre-authorisation.",
        ],
        bullets: [
          "A driving licence held for at least one year (EU licences accepted as-is).",
          "A passport or national ID card.",
          "Minimum age 21 for most of the fleet.",
          "A phone number we can reach you on during the rental.",
        ],
      },
      {
        heading: "Is no deposit really cheaper?",
        paragraphs: [
          "Compare the total, not just the daily rate. A €25/day offer with a €700 deposit, mandatory extra insurance at the counter and an airport surcharge frequently ends up more expensive than our all-in price with delivery and insurance included. Our rates start at €44/day with everything in the price and no counter upsell.",
        ],
      },
      {
        heading: "Booking a no-deposit car in Bucharest",
        paragraphs: [
          "Pick a car from the fleet, choose your dates and pickup point (our office on Bulevardul Regina Elisabeta 33, Otopeni Airport, or any custom address in Bucharest for a €20 delivery fee), and submit the booking. You receive a confirmation email immediately and a human reconfirmation before pickup. No card details, no deposit, no surprises.",
        ],
      },
    ],
  },
  {
    slug: "rent-a-car-without-credit-card-bucharest",
    title: "How to Rent a Car Without a Credit Card in Bucharest",
    metaTitle: "Rent a Car Without a Credit Card in Bucharest — Debit & Cash",
    description:
      "Renting a car in Bucharest without a credit card: debit card and cash accepted, no card pre-authorisation, no deposit. What documents you need and how pickup works.",
    excerpt:
      "Nearly every airport desk in Bucharest asks for a credit card in the driver's name. Here is how to rent with a debit card — or cash — instead.",
    datePublished: "2026-01-21",
    readingMinutes: 5,
    keywords: [
      "rent a car without credit card bucharest",
      "car rental no credit card romania",
      "debit card car rental otopeni",
    ],
    intro:
      "The credit card requirement is a leftover of the deposit system: agencies need a card they can freeze money on. Remove the deposit and the credit card stops being necessary. That is exactly how carentals.ro operates — debit cards and cash are both fine, and no card is ever pre-authorised.",
    sections: [
      {
        heading: "Why most companies insist on a credit card",
        paragraphs: [
          "Debit cards do not support long pre-authorisation holds the way credit cards do, so any company that relies on a deposit has to demand a credit card in the main driver's name. Travellers with only a debit card, a revolut-style account or a prepaid card are turned away at the desk — often after they have already paid a non-refundable booking online.",
        ],
      },
      {
        heading: "Paying without a credit card in Bucharest",
        paragraphs: ["We accept the payment methods people actually travel with."],
        bullets: [
          "Debit card (Visa / Mastercard, including digital banks).",
          "Cash in EUR or RON at pickup.",
          "Bank transfer for company and long-term rentals, with an invoice.",
          "No pre-authorisation, no card imprint, no blocked funds.",
        ],
      },
      {
        heading: "Documents you actually need",
        paragraphs: [
          "The driver present at handover must show a valid licence and an ID document. That is it — no proof of return flight, no utility bill, no credit history.",
        ],
        bullets: [
          "Driving licence, held at least 12 months.",
          "Passport or EU national ID.",
          "Age 21+ (23+ for the top of the luxury fleet).",
        ],
      },
      {
        heading: "Airport pickup at Otopeni without a credit card",
        paragraphs: [
          "Send us your flight number when you book. We track the flight, meet you in the short-term parking in front of Arrivals, hand over the keys in about ten minutes and take payment by debit card or cash on the spot. Delivery to Henri Coandă (OTP) is free.",
        ],
      },
      {
        heading: "What to watch out for elsewhere",
        paragraphs: [
          "If another supplier advertises 'no credit card needed', check three things before paying: whether a deposit is still taken in cash, whether the included insurance has an excess of several hundred euro, and whether the airport surcharge is added at the desk. Our price is the final price and the insurance excess policy is explained in writing before you sign.",
        ],
      },
    ],
  },
  {
    slug: "cheap-car-rental-bucharest-otopeni-airport",
    title: "Cheap Car Rental at Bucharest Otopeni Airport: Real Prices, No Hidden Fees",
    metaTitle: "Cheap Car Rental Otopeni Airport — No Deposit, No Fees",
    description:
      "What car rental at Bucharest Otopeni Airport really costs: free airport delivery, no deposit, no credit card, insurance and rovinieta included. Compare the total price.",
    excerpt:
      "The €9/day airport headline price is rarely the price you pay. Here is the full cost breakdown for renting at Otopeni — and how to avoid the extras.",
    datePublished: "2026-02-04",
    readingMinutes: 6,
    keywords: [
      "cheap car rental bucharest airport",
      "otopeni car rental no deposit",
      "rent a car otopeni airport",
    ],
    intro:
      "Bucharest Henri Coandă (OTP) is where the biggest gap between advertised and final rental prices in Romania appears. Understanding the four usual add-ons makes it easy to compare offers honestly.",
    sections: [
      {
        heading: "The four costs that inflate an airport rental",
        paragraphs: [
          "A low daily rate is usually recovered through extras added at the desk.",
        ],
        bullets: [
          "Airport surcharge / premium location fee: €5–€15 per rental.",
          "Deposit-reduction or 'super cover' insurance: €10–€25 per day.",
          "Second driver: €3–€6 per day.",
          "Out-of-hours pickup for late flights: €20–€30.",
        ],
      },
      {
        heading: "What is included in our Otopeni price",
        paragraphs: [
          "We quote one number per day and deliver to the airport for free.",
        ],
        bullets: [
          "Free delivery and collection at Otopeni Arrivals, day or night.",
          "Full insurance (CASCO + RCA) already in the rate.",
          "Rovinieta road vignette included.",
          "Unlimited mileage inside Romania on rentals of 3+ days.",
          "No deposit and no credit card requirement.",
        ],
      },
      {
        heading: "Getting from OTP into Bucharest",
        paragraphs: [
          "The airport is 17 km from the city centre on DN1 — 25 minutes off-peak, up to 45 in rush hour. Fuel is charged full-to-full, and the first petrol station is 4 km south of the terminal, which matters on the return leg.",
        ],
      },
      {
        heading: "How to compare offers fairly",
        paragraphs: [
          "Take the number of rental days, add every mandatory extra, add the insurance you would actually buy at the counter, and only then divide by the days. Also check the deposit: money frozen for 30 days has a real cost if it limits the rest of your trip. Our rates start at €44/day, all-in, with nothing blocked on your card.",
        ],
      },
    ],
  },
];

export function getBlogPost(slug: string) {
  return blogPosts.find((post) => post.slug === slug);
}
